/* import __COLOCATED_TEMPLATE__ from './performance-over-time.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { useResource } from 'ember-resources';
import ChartDataResourceCompatible from 'embercom/lib/reporting/chart-data-resource-compatible';
import SerieschartBuilder from 'embercom/lib/reporting/flexible/serieschart-builder';
import { isPresent } from '@ember/utils';
import moment from 'moment-timezone';
import Range from 'embercom/models/reporting/range';
import type RenderableChart from 'embercom/models/reporting/custom/renderable-chart';
import type ChartSeries from 'embercom/models/reporting/custom/chart-series';
import type { Point } from 'highcharts';
import { getChartSeriesName } from 'embercom/lib/reporting/custom/view-config-builder-helpers';

const CX_SCORE_METRIC_IDS = [
  'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
  'conversation.ai_generated_metrics.csat.positively_rated.count',
];

interface Signature {
  Args: {
    renderableChart: RenderableChart;
    dataConfig: any;
    viewConfig: any;
    range: any;
    openUnderlyingDataModal: (chartSeries: ChartSeries, range?: Range) => void;
  };
}

export default class PerformanceOverTime extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intercomEventService: any;

  @tracked tooltipOpenerElement: HTMLElement | undefined = undefined;
  @tracked hoveredPoint: Point | null = null;
  private _isPaywallActive = false;

  dataResource = useResource(this, ChartDataResourceCompatible, () => ({
    dataConfig: this.args.dataConfig,
    viewConfig: this.args.viewConfig,
    renderableChart: this.args.renderableChart,
  }));

  get app() {
    return this.appService.app;
  }

  get rawChartData() {
    return this.dataResource?.rawChartData || [];
  }

  get chartData() {
    return this.rawChartData.map((response: any, index: number) => {
      let metric = this.args.viewConfig.metrics?.[index];
      let seriesName = metric?.id || response.name;
      let aggregation = this.args.dataConfig.series[index]?.type;

      let data = this.buildVisualisationData(response);

      // Use getChartSeriesName for multimetric charts to get localized labels
      let legendLabel = getChartSeriesName(index, this.args.renderableChart) || seriesName;

      return {
        type: 'line' as const,
        name: seriesName,
        aggregation,
        data,
        legendLabel,
        marker: {
          symbol: 'circle',
        },
      };
    });
  }

  buildVisualisationData(dataResponse: any) {
    let group = dataResponse.groups?.[0];
    if (!group) {
      return [];
    }

    let aggregation = group.aggregations?.[0];
    if (!aggregation) {
      return [];
    }

    return group.values.map((value: any, index: number) => {
      let y = aggregation.values[index];
      return [value, y, value];
    });
  }

  get chartOptions() {
    let component = this;
    let options = {
      range: this.args.range,
      chartData: this.chartData,
      viewConfig: this.args.viewConfig,
      dataConfig: this.args.dataConfig,
      chartType: 'line',
      app: this.app,
      isMultimetric: this.args.renderableChart?.isMultimetric || false,
      isTimeComparison: this.args.renderableChart?.showTimeComparison || false,
    };

    if (this.args.viewConfig.seriesColors) {
      (options as any)['seriesColors'] = this.args.viewConfig.seriesColors;
    }

    let builder = new SerieschartBuilder(options);
    let chartOptions = builder.buildTheme();

    // Enable drill-in tooltip behavior
    let clickingEnabled = this.args.renderableChart.dateRange.inDays > 1;
    chartOptions['tooltip'].enabled = false;
    chartOptions['plotOptions'].series.states.hover.enabled = clickingEnabled;
    chartOptions['plotOptions'].line.cursor = clickingEnabled ? 'pointer' : undefined;
    chartOptions['plotOptions'].line.point = {
      events: {
        mouseOver(this: Point, event: PointerEvent) {
          component.hoveredPoint = this;
          component.tooltipOpenerElement = (event.target as any).graphic?.element;
          return false;
        },
        mouseOut(this: Point, _event: PointerEvent) {
          component.tooltipOpenerElement = undefined;
          return false;
        },
        click(this: Point, _event: PointerEvent) {
          if (clickingEnabled) {
            component.hoveredPoint = this;
            if (!(component._isPaywallActive && component.isHoveredPointCxScoreRelated)) {
              component.onViewConversations();
            }
          }
          return false;
        },
      },
    };

    return chartOptions;
  }

  get hoveredPointDate() {
    if (!this.hoveredPoint) {
      return;
    }
    let { dateRange, viewByTimeInterval } = this.args.renderableChart;

    let startDate = (this.hoveredPoint as any).x || (this.hoveredPoint as any).name;
    let startMoment = moment.tz(startDate, dateRange.timezone);
    let endMoment = startMoment.clone();

    let timeInterval = viewByTimeInterval || dateRange.interval;
    let dateTimeFormat = 'MMM D';
    switch (timeInterval) {
      case 'hour':
        dateTimeFormat = 'MMM D, ddd HH:mm';
        return startMoment.format(dateTimeFormat);
      case 'day':
        return startMoment.format(dateTimeFormat);
      case 'week':
        endMoment.add(1, 'weeks').subtract(1, 'days').endOf('day');
        break;
      case 'month':
        endMoment.add(1, 'months').subtract(1, 'days').endOf('day');
        break;
      default:
        return startMoment.format(dateTimeFormat);
    }
    // don't exceed the chart's date range
    startMoment = moment.max(startMoment, dateRange.startMoment);
    endMoment = moment.min(endMoment, dateRange.endMoment);
    return `${startMoment.format(dateTimeFormat)} - ${endMoment.format(dateTimeFormat)}`;
  }

  get tooltipMetricValues() {
    if (!this.hoveredPoint) {
      return [];
    }

    let { name, aggregation, legendLabel } = (this.hoveredPoint as any).series.userOptions;
    let metricId = name;
    let metricValue = (this.hoveredPoint as any).y;

    let formattedMetricValue = this.dataResource.formatValue({
      value: metricValue,
      metricId,
      aggregation,
    });

    return [
      {
        label: legendLabel,
        value: formattedMetricValue,
      },
    ];
  }

  get isHoveredPointCxScoreRelated(): boolean {
    if (!this.hoveredPoint) {
      return false;
    }
    let metricId = (this.hoveredPoint as any).series?.userOptions?.name;
    return CX_SCORE_METRIC_IDS.includes(metricId);
  }

  shouldShowUpgradeButton = (isPaywallActive: boolean): boolean => {
    this._isPaywallActive = isPaywallActive;
    return isPaywallActive && this.isHoveredPointCxScoreRelated;
  };

  get hasData() {
    return this.chartData.some((series: any) => {
      return series.data?.some(
        (dataArray: any) => dataArray[1] !== null && dataArray[1] !== undefined,
      );
    });
  }

  @action
  onViewConversations() {
    if (!this.hoveredPoint) {
      return;
    }

    let { dateRange, chartSeries } = this.args.renderableChart;
    let targetChartSeries = chartSeries
      .toArray()
      .findBy('metricId', (this.hoveredPoint as any).series.name);
    let pointIndex: number = (this.hoveredPoint as any).index;
    let nextPoint = (this.hoveredPoint as any).series.points[pointIndex + 1];
    let startDate =
      (this.hoveredPoint as any).index === 0
        ? moment.tz(dateRange.startMoment, dateRange.timezone)
        : moment.tz((this.hoveredPoint as any).x, dateRange.timezone);
    let endDate = isPresent(nextPoint)
      ? moment.tz(nextPoint.x, dateRange.timezone).subtract(1, 'day').endOf('day')
      : moment.tz(dateRange.endMoment, dateRange.timezone).endOf('day');
    let range = Range.createFromParams(startDate, endDate, dateRange.timezone);

    this.args.openUnderlyingDataModal(targetChartSeries, range);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::PerformanceOverTime': typeof PerformanceOverTime;
    'reporting/bespoke/fin-performance/performance-over-time': typeof PerformanceOverTime;
  }
}
